import { MAX_PIPELINE_PHASES_HARD } from '../lib/instanceFeatureLimits';

/** Project outcome — independent of pipeline stage. */
export const LEAD_OUTCOMES = ['open', 'won', 'lost'] as const;
export type LeadOutcome = (typeof LEAD_OUTCOMES)[number];

export const OUTCOME_STATUS_LABEL: Record<Exclude<LeadOutcome, 'open'>, string> = {
  won: 'Gewonnen',
  lost: 'Verloren',
};

/** @deprecated Prefer LeadOutcome; kept for status-string compatibility. */
export const TERMINAL_LEAD_STATUSES = ['Gewonnen', 'Verloren', 'Abgeschlossen'] as const;
export type TerminalLeadStatus = (typeof TERMINAL_LEAD_STATUSES)[number];

/** Default open phases when DB has none (matches previous product default). */
export const DEFAULT_OPEN_PIPELINE_PHASES: string[] = [
  'Neu',
  'Kontaktiert',
  'Termin vereinbart',
  'Angebot gesendet',
];

const TERMINAL_SET = new Set<string>(TERMINAL_LEAD_STATUSES);

export function isLeadOutcome(value: unknown): value is LeadOutcome {
  return value === 'open' || value === 'won' || value === 'lost';
}

export function outcomeFromStatus(status: string | null | undefined): LeadOutcome {
  const s = String(status ?? '').trim();
  if (s === 'Verloren') return 'lost';
  if (s === 'Gewonnen' || s === 'Abgeschlossen') return 'won';
  return 'open';
}

export function statusMirrorFromOutcomeAndLabel(
  outcome: LeadOutcome,
  stageLabel: string | null | undefined,
): string {
  if (outcome === 'won') return OUTCOME_STATUS_LABEL.won;
  if (outcome === 'lost') return OUTCOME_STATUS_LABEL.lost;
  const label = String(stageLabel ?? '').trim();
  return label || DEFAULT_OPEN_PIPELINE_PHASES[0]!;
}

export function displayStatusForLead(input: {
  outcome?: LeadOutcome | string | null;
  status?: string | null;
  stageLabel?: string | null;
}): string {
  const outcome = isLeadOutcome(input.outcome)
    ? input.outcome
    : outcomeFromStatus(input.status);
  if (outcome === 'won') return OUTCOME_STATUS_LABEL.won;
  if (outcome === 'lost') return OUTCOME_STATUS_LABEL.lost;
  const stage = String(input.stageLabel ?? '').trim();
  if (stage) return stage;
  return String(input.status ?? '').trim() || DEFAULT_OPEN_PIPELINE_PHASES[0]!;
}

/** All statuses for dropdowns: open phases + terminals (deduped). */
export function allSelectableLeadStatuses(openPhases: string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const s of openPhases) {
    const t = String(s ?? '').trim();
    if (!t || TERMINAL_SET.has(t) || seen.has(t)) continue;
    seen.add(t);
    out.push(t);
  }
  for (const t of ['Gewonnen', 'Verloren'] as const) {
    if (!seen.has(t)) {
      seen.add(t);
      out.push(t);
    }
  }
  return out;
}

/**
 * Normalize pipeline from API/DB: non-empty unique labels, strip terminals, hard max phases, max label length 120.
 */
export function normalizeOpenPipelinePhases(raw: unknown): string[] {
  let arr: unknown[];
  if (raw == null) return [...DEFAULT_OPEN_PIPELINE_PHASES];
  if (Array.isArray(raw)) arr = raw;
  else if (typeof raw === 'string') {
    try {
      const p = JSON.parse(raw) as unknown;
      arr = Array.isArray(p) ? p : [];
    } catch {
      return [...DEFAULT_OPEN_PIPELINE_PHASES];
    }
  } else return [...DEFAULT_OPEN_PIPELINE_PHASES];

  const seen = new Set<string>();
  const out: string[] = [];
  for (const x of arr) {
    const s = String(x ?? '').trim();
    if (!s || s.length > 120) continue;
    if (TERMINAL_SET.has(s)) continue;
    if (seen.has(s)) continue;
    seen.add(s);
    out.push(s);
    if (out.length >= MAX_PIPELINE_PHASES_HARD) break;
  }
  return out.length > 0 ? out : [...DEFAULT_OPEN_PIPELINE_PHASES];
}

export function isAllowedLeadStatus(status: string, openPhases: string[]): boolean {
  if (TERMINAL_SET.has(status)) return true;
  return openPhases.includes(status);
}

export function normalizeStageLabels(raw: unknown): string[] {
  return normalizeOpenPipelinePhases(raw);
}

/** Drop zone IDs for dnd-kit (avoid collision with phase labels / stage ids). */
export const DND_TERMINAL_WON_ID = '__lead_terminal_gewonnen__';
export const DND_TERMINAL_LOST_ID = '__lead_terminal_verloren__';

/** Legacy drop zone id — still accepted when reading drag events. */
export const DND_TERMINAL_WON_ID_LEGACY = '__lead_terminal_abgeschlossen__';
